import React, { useState, useEffect } from 'react'

import { Icon, Search as SearchIcon, InfoOutlined as InfoIcon, Modal } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, string } from 'prop-types'

import ImageSearch from 'Pods/Shared/ImageSearch/ImageSearch'
import Tooltip from 'Pods/Shared/Tooltip'
import { useSearch } from 'Pods/UnifiedNavigation/Hooks/useSearch'

import useBreakpoint from 'Utils/useBreakpoint'

import SuggestionRow from './Components/SuggestionRow/SuggestionRow'

let SearchAdvancedTips = null

function Search({
  isInListingPage,
  searchInputValue = null,
  enableAdvancedKeywordsSearch = false,
  searchPlaceholder,
  locale = 'en',
}) {
  const {
    query,
    wrapperRef,
    suggestions,
    showSuggestions,
    onInputChange,
    handleKeyDown,
    submitting,
    handleSubmit,
    cursorPosition,
  } = useSearch(isInListingPage, searchInputValue)
  const [isAdvancedSearchTipsModalOpen, setIsAdvancedSearchTipsModalOpen] = useState(false)
  const [loadedComponent, setLoadedComponent] = useState(false)
  const [hasBeenHovered, setHasBeenHovered] = useState(false)
  const [hasHydrated, setHasHydrated] = useState(false)

  const currentBreakpoint = useBreakpoint()
  const isMobileBreakpoint = ['xs', 'sm'].includes(currentBreakpoint)
  const isMobile = hasHydrated ? isMobileBreakpoint : false

  useEffect(() => {
    setHasHydrated(true)
  }, [])

  useEffect(() => {
    if (enableAdvancedKeywordsSearch && !loadedComponent && hasBeenHovered) {
      import('Pods/LazyLoaded/SearchAdvancedTips/SearchAdvancedTips')
        .then((module) => {
          SearchAdvancedTips = module.default
          setLoadedComponent(true)
        })
        .catch((error) => {
          if (window.Sentry) {
            window.Sentry.captureException(error)
          }
        })
    }
  }, [enableAdvancedKeywordsSearch, loadedComponent, hasBeenHovered])

  function handleMouseEnter() {
    setHasBeenHovered(true)
  }

  function handleShowAdvancedSearchTips() {
    // Only handle modal opening on mobile/tablet
    if (isMobile) {
      setIsAdvancedSearchTipsModalOpen(true)
    }
    // Desktop tooltip is handled by the Tooltip component itself
  }

  function handleHideAdvancedSearchTips() {
    setIsAdvancedSearchTipsModalOpen(false)
  }

  return (
    <div
      className="wayfinder__search js-remaining-filters"
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
    >
      <ImageSearch locale={locale} />
      {isMobile && loadedComponent && (
        <Modal
          size="small"
          header="Search Tips & Tricks"
          isHeaderSeparated
          isOpen={isAdvancedSearchTipsModalOpen}
          onClose={() => handleHideAdvancedSearchTips()}
        >
          <SearchAdvancedTips locale={locale} />
        </Modal>
      )}
      <form
        className="wayfinder__form"
        id="nav-search-form"
        acceptCharset="UTF-8"
        action="/search"
        method="get"
        onSubmit={handleSubmit}
      >
        <input
          type="text"
          name="keywords"
          className="wayfinder__search-input"
          placeholder={searchPlaceholder}
          autoComplete="off"
          value={query}
          onChange={onInputChange}
          onKeyDown={handleKeyDown}
        />
        {isMobile ? (
          <Icon svgComponent={InfoIcon} color="dark" onClick={handleShowAdvancedSearchTips} />
        ) : (
          <Tooltip
            id="search-tips"
            tooltipClassName="advanced-tips__tooltip"
            content={loadedComponent ? <SearchAdvancedTips showTitle locale={locale} /> : null}
            place="bottom"
            type="light"
          >
            <Icon svgComponent={InfoIcon} color="dark" />
          </Tooltip>
        )}
        <button
          className="cgt-button cgt-button--primary cgt-button--medium cgt-button--only-icon wayfinder__search-button"
          aria-label="Search"
        >
          {submitting ? (
            <div className="cgt-icon cgt-icon__icon cgt-icon--default cgt-icon--primary">
              <div className="cgt-spinner cgt-spinner--xsmall site-search__loader" />
            </div>
          ) : (
            <Icon notSpaced svgComponent={SearchIcon} color="dark" />
          )}
        </button>
      </form>
      {suggestions.length > 0 && showSuggestions && (
        <div className="wayfinder__suggestions">
          <ul className="wayfinder__suggestions-results">
            {suggestions.map((resultRow, i) => (
              <li key={resultRow[0]}>
                <SuggestionRow
                  query={query}
                  result={resultRow[0]}
                  thumbnailUrl={resultRow[1].thumbnailUrl}
                  className={i === cursorPosition ? 'is-active' : ''}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

Search.propTypes = {
  isInListingPage: bool.isRequired,
  searchInputValue: string,
  searchPlaceholder: string.isRequired,
  enableAdvancedKeywordsSearch: bool,
  locale: string,
}

export default Search
